import { POST } from ".";

export interface StripePaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  funding: string;
  isDefault: boolean;
  declined?: boolean;
  declinedAt?: string;
  declinedCode?: string;
}

export interface StripeInvoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
  hostedInvoiceUrl: string | null;
  description: string | null;
}

export interface GetPaymentMethodsResponse {
  paymentMethods: StripePaymentMethod[];
  defaultPaymentMethodId: string | null;
}

export interface GetInvoiceHistoryResponse {
  invoices: StripeInvoice[];
}

export interface CreateSetupIntentResponse {
  type: "setup";
  clientSecret: string;
}

const StripePaymentServices = {
  /**
   * Get all payment methods (cards) for the user from Stripe
   */
  getPaymentMethods: async () => {
    return POST<GetPaymentMethodsResponse>("user/get-payment-methods");
  },

  /**
   * Delete a payment method from Stripe
   */
  deletePaymentMethod: async (paymentMethodId: string) => {
    return POST<{ ok: boolean }>("user/delete-payment-method", {
      paymentMethodId,
    });
  },

  /**
   * Set a payment method as the default for the customer
   */
  setDefaultPaymentMethod: async (paymentMethodId: string) => {
    return POST<{ ok: boolean }>("user/set-default-payment-method", {
      paymentMethodId,
    });
  },

  /**
   * Get invoice history for the user from Stripe
   */
  getInvoiceHistory: async () => {
    return POST<GetInvoiceHistoryResponse>("user/get-invoice-history");
  },

  /**
   * Create a setup intent for adding a new card
   */
  createSetupIntent: async () => {
    return POST<CreateSetupIntentResponse>("user/create-setup-intent", {});
  },

  /**
   * Set default payment method from a setup intent (after adding new card)
   * Also retries payment if user has paymentFailed status
   */
  setDefaultPaymentMethodFromSetupIntent: async (setupIntentId: string) => {
    return POST<{
      ok: boolean;
      paymentRetried?: boolean;
      paymentSucceeded?: boolean;
    }>("user/set-default-payment-method-from-setup-intent", {
      setupIntentId,
    });
  },
};

export default StripePaymentServices;
